/**
 * Notification type to icon mapping
 * Uses inline SVG icons matching the project pattern (no external icon library)
 */
import type { NotificationType } from '@/services/notifications';

// ============================================================================
// Icon Components
// ============================================================================

interface IconProps {
  className?: string;
}

function ArrowRightLeftIcon({ className }: IconProps) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M7.5 21L3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5"
      />
    </svg>
  );
}

function RefreshIcon({ className }: IconProps) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99"
      />
    </svg>
  );
}

function UserPlusIcon({ className }: IconProps) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M19 7.5v3m0 0v3m0-3h3m-3 0h-3m-2.25-4.125a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zM4 19.235v-.11a6.375 6.375 0 0112.75 0v.109A12.318 12.318 0 0110.374 21c-2.331 0-4.512-.645-6.374-1.766z"
      />
    </svg>
  );
}

function AlertTriangleIcon({ className }: IconProps) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
      />
    </svg>
  );
}

function BellIcon({ className }: IconProps) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0"
      />
    </svg>
  );
}

function DollarSignIcon({ className }: IconProps) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  );
}

function LinkOffIcon({ className }: IconProps) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M13.181 8.68a4.503 4.503 0 011.903 1.415M6.166 12.986a4.5 4.5 0 001.657 6.096m6.358-7.093l-.198.198M16.5 7.5a4.5 4.5 0 00-4.5-4.5m4.5 4.5v0M3 3l18 18"
      />
    </svg>
  );
}

function XMarkIcon({ className }: IconProps) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M6 18L18 6M6 6l12 12"
      />
    </svg>
  );
}

function CheckDoubleIcon({ className }: IconProps) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  );
}

// ============================================================================
// Icon Configuration
// ============================================================================

interface NotificationIconConfig {
  icon: React.FC<IconProps>;
  colorClass: string;
  bgClass: string;
}

/**
 * Get the icon configuration for a notification type
 */
export function getNotificationIconConfig(type: NotificationType | string): NotificationIconConfig {
  switch (type) {
    case 'transfers_detected':
      return {
        icon: ArrowRightLeftIcon,
        colorClass: 'text-orange-500',
        bgClass: 'bg-orange-50',
      };

    case 'sync_complete':
      return {
        icon: RefreshIcon,
        colorClass: 'text-green-500',
        bgClass: 'bg-green-50',
      };

    case 'sync_failed':
      return {
        icon: RefreshIcon,
        colorClass: 'text-red-500',
        bgClass: 'bg-red-50',
      };

    case 'invitation_accepted':
    case 'member_left':
      return {
        icon: UserPlusIcon,
        colorClass: 'text-blue-500',
        bgClass: 'bg-blue-50',
      };

    case 'balance_mismatch':
    case 'subscription_expiring':
      return {
        icon: AlertTriangleIcon,
        colorClass: 'text-amber-500',
        bgClass: 'bg-amber-50',
      };

    case 'new_transactions':
      return {
        icon: DollarSignIcon,
        colorClass: 'text-green-500',
        bgClass: 'bg-green-50',
      };

    case 'account_disconnected':
      return {
        icon: LinkOffIcon,
        colorClass: 'text-red-500',
        bgClass: 'bg-red-50',
      };

    default:
      return {
        icon: BellIcon,
        colorClass: 'text-gray-500',
        bgClass: 'bg-gray-50',
      };
  }
}

// ============================================================================
// Exported Icons for Use in Other Components
// ============================================================================

export { BellIcon, XMarkIcon, CheckDoubleIcon };

// ============================================================================
// NotificationIcon Component
// ============================================================================

interface NotificationIconProps {
  type: NotificationType | string;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

/**
 * Render the appropriate icon for a notification type
 */
export function NotificationIcon({ type, size = 'md', className = '' }: NotificationIconProps) {
  const config = getNotificationIconConfig(type);
  const Icon = config.icon;

  const sizeClasses = {
    sm: 'h-4 w-4',
    md: 'h-5 w-5',
    lg: 'h-6 w-6',
  };

  const containerSizeClasses = {
    sm: 'h-7 w-7',
    md: 'h-9 w-9',
    lg: 'h-11 w-11',
  };

  return (
    <div
      className={`${containerSizeClasses[size]} ${config.bgClass} rounded-full flex items-center justify-center flex-shrink-0 ${className}`}
    >
      <Icon className={`${sizeClasses[size]} ${config.colorClass}`} />
    </div>
  );
}
